// ******************** LAST EDIT - 2/17/22 ************************************

import {subclass, property} from "esri/core/accessorSupport/decorators";
import DirectionsViewModel = require("esri/widgets/Directions/DirectionsViewModel");
import EsriError = require("esri/core/Error");
import GraphicsLayer = require("esri/layers/GraphicsLayer");
// @ts-ignore
import { isAbortError } from "esri/core/promiseUtils";
// @ts-ignore
import { watch } from "esri/core/reactiveUtils";

import RouteLayer = require("esri/layers/RouteLayer");
import RouteStopSymbols = require("esri/layers/support/RouteStopSymbols");
import RouteSymbols = require("esri/layers/support/RouteSymbols");
import { PictureMarkerSymbol, SimpleLineSymbol, SimpleMarkerSymbol } from "esri/symbols";
// @ts-ignore
import { getAccentColor } from "esri/core/analysisThemeUtils";

const enum ServiceInfoStatus {
  Idle,
  Active,
  Complete,
  Failed,
  Suspended
}

@subclass("app.route.core.DirectionsViewModel")
export default class CustomDirectionsViewModel extends DirectionsViewModel {

  indoorsContext;
  indoorsCustomDirections;
  indoorsEventSymbolHighlight;
  indoorsRouteServiceUrl;
  indoorsRouteResult: any;
  indoorsRouteSymbol;
  indoorsRouteSymbolAlternate;
  indoorsRouteSymbolHighlight;
  indoorsRouteSymbolTransition;
  indoorsRouteSymbolFor3D;
  tsUtil;

  _highlightLayer;
  _routeGraphicsLayer;

  private _serviceDescriptionLoadStatus: ServiceInfoStatus = ServiceInfoStatus.Idle;

  constructor(params?: any) {
    super(params);
    this.indoorsContext = params.indoorsContext;
    this.indoorsRouteServiceUrl = params.indoorsRouteServiceUrl;
    this.tsUtil = params.tsUtil;
    this._highlightLayer = new GraphicsLayer({ listMode: "hide" });
    this._routeGraphicsLayer = new GraphicsLayer({ listMode: "hide" });
    this.layer = this._getLegacyLayer();
    this.addHandles([
      watch(
        () => this.lastRoute, 
        () => {
          this.indoorsAddRouteToLayer()
        }
      )
    ])
  }

  clearHighlights(): void {
    super.clearHighlights();
    if (this._highlightLayer) this._highlightLayer.removeAll();
  }

  @property({
    dependsOn: ["serviceDescription"]
  })
  // @ts-ignore
  get selectedTravelMode() {
    const { serviceDescription } = this;
    if (!serviceDescription) {
      return null;
    }
    const { defaultTravelMode, supportedTravelModes = [] } = serviceDescription;
    let travelMode = null;
    if (this.indoorsContext && supportedTravelModes) {
      const selectedID = this.indoorsContext.session.selectedTravelModeID;
      if (selectedID !== undefined && selectedID !== null) {
        for (let i = 0; i < supportedTravelModes.length; i++) {
          if (supportedTravelModes[i].id === selectedID) {
            let tm = supportedTravelModes[i];
            if (tm) return tm;
          }
        }
      }
    }
    travelMode = defaultTravelMode || serviceDescription.defaultTravelMode || supportedTravelModes[0];
    return travelMode || null;
  }

  // @ts-ignore
  @property() async getDirections() {
    // @ts-ignore
    const { layer, state } = this;

    if (
      // @ts-ignore
      state === "unauthenticated" ||
      // @ts-ignore
      state === "initializing" ||
      state === "disabled" ||
      this._serviceDescriptionLoadStatus === ServiceInfoStatus.Failed
    ) {
      // @ts-ignore
      return Promise.reject(new EsriError("directions-view-model:not-loaded", "Cannot get directions until view model loads."));
    }

    this._set("lastError", null);

    // @ts-ignore
    if (this._routeController != null) {
      // @ts-ignore
      this._routeController.abort();
      // @ts-ignore
      this._routeController = null;
    }

    const stopCount = layer.stops.filter(({ geometry }) => geometry != null).length;
    if (stopCount < 2) {
      // @ts-ignore
      const error = new EsriError("directions-view-model:not-enough-stops", "Not enough stops for routing");
      this._set("lastError", error);
      // @ts-ignore
      return Promise.reject(error);
    }

    // @ts-ignore
    const { apiKey, departureTime } = this;
    const startTimeIsUTC = departureTime === "now";
    const startTime = departureTime === "now" ? new Date() : departureTime;
    if (startTime && !startTimeIsUTC) {
      const timezoneOffsetInMillis = startTime.getTimezoneOffset() * 60 * 1000;
      startTime.setTime(startTime.getTime() - timezoneOffsetInMillis);
    }
    const outSpatialReference = this.view != null ? this.view.spatialReference : null;

    // @ts-ignore
    const routeParams = this.routeParameters.clone();
    // @ts-ignore
    let dirlang = this._directionsLanguage
    routeParams.set({
      startTime,
      startTimeIsUTC,
      directionsLanguage: dirlang,
      apiKey,
      outSpatialReference,
      stops: layer.stops
    });
    this.indoorsCheckLocale(routeParams);
    if (this.selectedTravelMode != null) {
      routeParams.travelMode = this.selectedTravelMode;
    }
    this.indoorsContext.aiim.routeBarriers.addBarriers(routeParams);

    // @ts-ignore
    this._routeController = new AbortController();
    // @ts-ignore
    const { signal } = this._routeController;

    let solution = null;
    try {
      const customSolve = this.indoorsContext.lib.indoors.customSolve;
      const rs = this.indoorsContext.aiim.routeService;
      const li = rs && rs.info && rs.info.layerInfo;
      const supportedTravelModes = li && li.supportedTravelModes;
      const url = this.indoorsRouteServiceUrl;
      const response = await customSolve.solve(supportedTravelModes,url,routeParams,{signal})
      this.indoorsRouteResult = this.tsUtil.newRouteResult({
        response: response,
        viewModel: this
      });
      this.indoorsRouteResult.process();
      this._set("lastRoute", response); // @todo this isn't correct, it should be a solution
      this.zoomToRoute();
    } catch (error) {
      if (!isAbortError(error)) {
        // @ts-ignore
        const esriError = new EsriError("directions-view-model:unable-to-route", "Unable to route to these addresses", {error});
        this._set("lastError", esriError);
        this._set("lastRoute", null);
        throw esriError;
      }
    } finally {
      // @ts-ignore
      this._routeController = null;
    }
    return solution;
  }

  private _getLegacyLayer(): RouteLayer {
    const color = getAccentColor();

    return new RouteLayer({
      listMode: "hide",
      defaultSymbols: new RouteSymbols({
        directionLines: new SimpleLineSymbol({
          color,
          width: 7,
          cap: "round",
          join: "round"
        }),
        directionPoints: null,
        routeInfo: null,
        stops: new RouteStopSymbols({
          break: new SimpleMarkerSymbol({
            color: [255, 255, 255],
            size: 10,
            outline: {
              color: [20, 89, 127],
              width: 2.5
            }
          }),
          first: new SimpleMarkerSymbol({
            color,
            size: 19,
            outline: {
              color: [51, 51, 51],
              width: 3
            }
          }),
          last: new PictureMarkerSymbol({
            width: 23,
            height: 23,
            url: `data:image/svg+xml;base64,${btoa(
              `<svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg"><g fill-rule="nonzero" fill="none"><path d="M15.15.3C6.9.3.3 6.9.3 15.15S6.9 29.7 15.15 29.7 29.7 23.1 29.7 15.15C29.7 6.9 23.25.3 15.15.3z" fill="#333"/><path d="M15 4.8C9.3 4.8 4.8 9.45 4.8 15c0 5.55 4.65 10.2 10.2 10.2 5.55 0 10.2-4.5 10.2-10.2 0-5.55-4.5-10.2-10.2-10.2z" fill="${color.toHex()}"/><path fill="#333" d="M10.5 10.5h9v9h-9z"/></g></svg>`
            )}`
          }),
          middle: new SimpleMarkerSymbol({
            color: [51, 51, 51],
            size: 12,
            outline: {
              color,
              width: 3
            }
          }),
          unlocated: new PictureMarkerSymbol({
            height: 18,
            width: 18,
            url: `data:image/svg+xml;base64,${btoa(
              `<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><path d="M10.1.2C4.6.2.2 4.6.2 10.1s4.4 9.7 9.9 9.7 9.7-4.4 9.7-9.7c0-5.5-4.3-9.9-9.7-9.9z" fill="#333" fill-rule="nonzero"/><path d="M10 2.7c-4.08 0-7.3 3.328-7.3 7.3s3.328 7.3 7.3 7.3 7.3-3.22 7.3-7.3c0-3.972-3.22-7.3-7.3-7.3z" fill="${color.toHex()}" fill-rule="nonzero"/><path d="M11.414 10l5.304-5.303-1.415-1.415L10 8.586 4.697 3.282 3.282 4.697 8.586 10l-5.304 5.303 1.415 1.415L10 11.414l5.303 5.304 1.415-1.415L11.414 10z" fill="#333"/></g></svg>`
            )}`
          }),
          waypoint: new SimpleMarkerSymbol({
            color: [255, 255, 255],
            size: 10,
            outline: {
              color: [20, 89, 127],
              width: 2.5
            }
          })
        })
      }),
      stops: [{}, {}]
    });
  }

  @property()
  zoomToRoute(): void {
    // *** Indoors ******************************
    if (this.indoorsRouteResult) {
      this.indoorsRouteResult.goToRoute();
    }
    // ******************************************
  }

  /* *** Indoors ============================================================ */

  indoorsAddRouteToLayer(): void {
    if (this.indoorsRouteResult) {
      const view: any = this.get("view");
      const routeGraphicsLayer: any = this.get("_routeGraphicsLayer");
      routeGraphicsLayer.graphics.removeAll();
      this.indoorsRouteResult.addRouteGraphics(view,routeGraphicsLayer.graphics);
    }
  }

  indoorsCheckLocale(routeParameters) {
    const info = this.serviceDescription;
    if (routeParameters && routeParameters.directionsLanguage &&
        // @ts-ignore
        info && info.directionsSupportedLanguages) {
      const lc = routeParameters.directionsLanguage.toLowerCase();
      // @ts-ignore
      info.directionsSupportedLanguages.some(key => {
        if (lc === key.toLowerCase()) {
          if (routeParameters.directionsLanguage !== key) {
            routeParameters.directionsLanguage = key;
          }
          return true;
        }
        return false;
      });
    }
  }

  indoorsRemoveResult() {
    this.indoorsRouteResult = null;
    this._set("lastRoute", null);
    const routeGraphicsLayer: any = this.get("_routeGraphicsLayer");
    routeGraphicsLayer.graphics.removeAll();
  }

}
